import * as React from 'react'
import {useEffect} from 'react'

import {
  useFederationServers,
  useProjectsForSidebar,
  useSearchProjectsTreeMerged,
} from '../../../../../hooks/projectTrees'
import {useAppDispatch, useAppSelector} from '../../../../../hooks/react-redux'
import {usePermission} from '../../../../../hooks/usePermission'
import {Permission, ROOT_PROJECT_ID} from '../../../../../types'
import {emptyMap} from '../../../../../utils/empty'
import {EditProjectSidebarContext} from '../../../../common/EditProjectSidebar/EditProjectSidebar.context'
import EditProjectSidebarDialog from '../../../../common/EditProjectSidebar/EditProjectSidebarDialog'
import ProjectsTree from '../../../../common/ProjectsTree/ProjectsTree'
import SidebarFooter from '../../../../common/SidebarFooter/SidebarFooter'
import SidebarSearch from '../../../../common/SidebarSearch/SidebarSearch'
import type {SidebarSearchOwnProps} from '../../../../common/SidebarSearch/SidebarSearch.types'
import SidebarHeader from '../SidebarHeader/SidebarHeader'
import SidebarHeaderShadow from '../SidebarHeaderShadow/SidebarHeaderShadow'
import {useSidebarScroll} from '../SidebarPanelContent.hooks'
import {getSidebarSearchPlaceholder} from '../SidebarPanelContent.utils'

import {expandParents} from './ProjectsSidebar.actions'
import {useProjectsSidebar} from './ProjectsSidebar.hooks'
import type {Props} from './ProjectsSidebar.types'

import styles from './ProjectsSidebar.css'

function ProjectsSidebar({
  statuses,
  activeItem,
  changeActiveItem,
  toggleExpandState,
  toggleExpandAll,
  width,
  toggleOverview,
  reloadProjects,
  collapsed,
  toggleCollapseSidebar,
  className,
  ready,
  federationServersKey,
}: Props) {
  const dispatch = useAppDispatch()

  const [scrollState, onScroll] = useSidebarScroll()
  const {toggleCollapseSidebarAction, onSelect, ...pipelinesSidebarSearch} = useProjectsSidebar(
    collapsed,
    toggleCollapseSidebar,
  )
  const {searchQuery, searchActive} = pipelinesSidebarSearch

  const projectsForSidebar = useProjectsForSidebar()
  const federationServers = useFederationServers(federationServersKey)
  const searchProjectsTreeMerged = useSearchProjectsTreeMerged(federationServersKey)
  const userCanEdit = usePermission(Permission.CHANGE_OWN_PROFILE, ROOT_PROJECT_ID)

  const isEdit = React.useContext(EditProjectSidebarContext)

  const withArchived = useAppSelector(state => state.projectsSidebar.showArchivedProjects)
  useEffect(() => {
    reloadProjects(federationServers, withArchived)
  }, [reloadProjects, federationServers, withArchived])

  const items = searchActive && searchQuery ? searchProjectsTreeMerged.data : projectsForSidebar
  const searchProjectsTreeHash = searchProjectsTreeMerged.hash || emptyMap

  const filteredItems = React.useMemo(
    () =>
      isEdit
        ? items.filter(item => item.itemType !== 'link' && item.id !== ROOT_PROJECT_ID)
        : items,
    [isEdit, items],
  )

  useEffect(() => {
    if (ready && activeItem != null) {
      dispatch(expandParents(activeItem, searchProjectsTreeHash))
    }
  }, [activeItem, dispatch, ready, searchProjectsTreeHash])

  const renderSearch = ({sidebarRef, ...searchProps}: SidebarSearchOwnProps) => (
    <SidebarSearch
      wrapperClassName={styles.searchWrapper}
      placeholder={getSidebarSearchPlaceholder(width, 'Search by name')}
      {...pipelinesSidebarSearch}
      {...searchProps}
    />
  )

  const renderSearchWithWrapper = (props: SidebarSearchOwnProps) => (
    <SidebarHeaderShadow
      sidebarCollapsed={collapsed}
      showShadow={!collapsed && scrollState?.isScrollAtStart === false}
    >
      <SidebarHeader
        className={styles.header}
        sidebarCollapsed={collapsed}
        toggleCollapseSidebar={toggleCollapseSidebarAction}
        search={renderSearch(props)}
      />
    </SidebarHeaderShadow>
  )

  return (
    <div className={styles.sidebar}>
      <ProjectsTree
        contentClassName={styles.sidebarContent}
        items={filteredItems}
        statuses={statuses}
        activeItem={activeItem}
        changeActiveItem={changeActiveItem}
        toggleExpandState={(projectId, expanded, group) =>
          toggleExpandState(projectId, expanded, group, searchProjectsTreeHash)
        }
        toggleExpandAll={toggleExpandAll}
        searchQuery={searchQuery}
        searchActive={searchActive}
        width={width}
        toggleOverview={toggleOverview}
        reloadProjects={() => reloadProjects(federationServers, withArchived)}
        collapsed={collapsed}
        className={className}
        ready={ready}
        userCanEdit={userCanEdit}
        dispatch={dispatch}
        onSelect={onSelect}
        onScroll={onScroll}
        renderSearch={renderSearchWithWrapper}
      />
      {!searchActive ? null : <SidebarFooter />}
      {userCanEdit && <EditProjectSidebarDialog projectId={ROOT_PROJECT_ID} />}
    </div>
  )
}

export default ProjectsSidebar
